import type { CSSObject } from '@emotion/react';
import type { Theme } from '@mui/material';
import { tss } from 'tss-react/mui';

import {
  SIDEBAR_WIDTH_COLLAPSED,
  SIDEBAR_WIDTH_EXPANDED
} from '@/constants/sidebar.constants';

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.leavingScreen,
    easing: theme.transitions.easing.sharp
  }),
  width: SIDEBAR_WIDTH_COLLAPSED
});

const openedMixin = (theme: Theme): CSSObject => ({
  overflowX: 'hidden',
  transition: theme.transitions.create('width', {
    duration: theme.transitions.duration.enteringScreen,
    easing: theme.transitions.easing.sharp
  }),
  width: SIDEBAR_WIDTH_EXPANDED
});

const useStyles = tss
  .withParams<{
    isLayoutMobile?: boolean;
    isSidebarExpanded: boolean;
    organizationsLengthEqualToOne: boolean;
  }>()
  .create(
    ({
      theme,
      isLayoutMobile,
      isSidebarExpanded,
      organizationsLengthEqualToOne
    }) => ({
      sidebar: {
        '& .MuiPaper-root': {
          backgroundColor: theme.vars.palette.background.navigation,
          border: 0,
          width: SIDEBAR_WIDTH_EXPANDED
        },
        // desktop styles
        ...(!isLayoutMobile && {
          boxSizing: 'border-box',
          flexShrink: 0,
          position: 'fixed',
          ...(isSidebarExpanded && {
            ...openedMixin(theme),
            '& .MuiDrawer-paper': openedMixin(theme)
          }),
          ...(!isSidebarExpanded && {
            ...closedMixin(theme),
            '& .MuiDrawer-paper': {
              ...closedMixin(theme),
              overflowY: 'visible'
            }
          }),
          width: isSidebarExpanded
            ? SIDEBAR_WIDTH_COLLAPSED
            : SIDEBAR_WIDTH_EXPANDED,
          zIndex: 1000
        })
      },

      sidebarHeader: {
        alignItems: 'center',
        display: 'flex',
        justifyContent:
          isSidebarExpanded || isLayoutMobile ? 'start' : 'center',
        minHeight: '4rem !important',
        padding:
          isSidebarExpanded || isLayoutMobile ? theme.spacing(0, 0, 0, 4) : 0,
        // desktop styles
        ...((!isLayoutMobile || organizationsLengthEqualToOne) && {
          borderBottom: '1px solid rgba(255, 255, 255, 0.15)',
          marginBottom: theme.spacing(2)
        })
      },
      sidebarLogo: {
        height: '2.5rem',
        width: 'auto'
      },
      sidebarButton: {
        display: 'block'
      },
      sidebarButtonBadge: {
        position: 'absolute',
        top: theme.spacing(2),
        left: theme.spacing(5.75)
      },
      sidebarButtonButton: {
        display: 'flex',
        height: theme.spacing(6.5),
        justifyContent:
          isSidebarExpanded || isLayoutMobile ? 'space-between' : 'center',
        padding:
          isSidebarExpanded || isLayoutMobile
            ? theme.spacing(1, 3, 1, 3)
            : theme.spacing(0),
        position: 'relative',
        '& div:nth-of-type(1)': {
          display: 'flex'
        },
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)'
        }
      },
      sidebarButtonIcon: {
        alignItems: 'center',
        display: 'flex',
        justifyContent: 'center',
        color: 'rgba(255, 255, 255, 0.70)',
        fontSize:
          isSidebarExpanded || isLayoutMobile
            ? '1.25rem'
            : '1.75rem !important',
        minWidth: isSidebarExpanded || isLayoutMobile ? theme.spacing(4) : 0,
        width: isSidebarExpanded || isLayoutMobile ? 'auto' : '100%',
        '.svg-inline--fa': {
          fontSize:
            isSidebarExpanded || isLayoutMobile
              ? '1.25rem'
              : '1.5rem !important'
        }
      },
      sidebarButtonText: {
        '& > .MuiTypography-root': {
          color: 'rgba(255, 255, 255, 0.60)',
          display: isSidebarExpanded || isLayoutMobile ? 'block' : 'none',
          fontSize: '1rem',
          fontWeight: 500,
          marginLeft: theme.spacing(0.5)
        }
      },
      sidebarButtonNavLink: {
        position: 'relative',
        textDecoration: 'none',
        zIndex: 0,
        '&.active': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
          '.MuiListItemButton-root': {
            background: 'rgba(255, 255, 255, 0.15)'
          },
          '& .MuiListItemIcon-root': {
            color: theme.vars.palette.common.white
          },
          '& .MuiTypography-root': {
            color: theme.vars.palette.common.white
          },
          '.svg-inline--fa': {
            opacity: 1
          }
        }
      },
      sidebarButtonTooltip: {
        backgroundColor: theme.vars.palette.background.navigation,
        fontSize: '0.875rem'
      },
      sidebarButtonTooltipArrow: {
        color: theme.vars.palette.background.navigation
      }
    })
  );

export default useStyles;
